//reducers
import { selectTranslations } from 'state/reducers/translation.reducers';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getBeneficiaryById } from 'state/reducers/beneficiary.reducers';
//components
import { FormsStack } from 'theme/elements/Forms';
import { InfoCard } from 'theme/elements/Cards';
import { FormHeader1 } from 'theme/elements/Headers';
//mui
import { Stack } from '@mui/system';
import { Button, useTheme } from '@mui/material';
import { FormButton } from 'theme/elements/Buttons';
import { pagesEnum, timing } from 'constants/ui.constants';
import { useNavigate, useParams } from 'react-router-dom';
import {
	BeneficiaryDetailsBankAccount,
	paymentsMethodsDisplayList,
	PaymentCategoryOption,
} from 'constants/beneficiary.constants';
import BeneficiaryNameWithFlag from 'sections/beneficiary/details/BeneficiaryNameWithFlag';
import { Pay, PayEnum, paymentReasonList, paymentsTypes } from 'constants/pay.constants';
import { formatPaymentCategory } from 'utils/paymentCategoryUtils';
import { getNewPay, toggleUpserting } from 'state/reducers/pay.reducers';
import { Paragraph3 } from 'theme/elements/Paragraphs';
import { getCountries, getCurrencies } from 'state/reducers/global.reducers';
import { format, isValid } from 'date-fns';
import _ from 'lodash';
import { Fragment } from 'react';
import { getFile } from 'services/global.services';
import { getFormattedNumber } from 'utils';
import { getPaymentById } from 'state/reducers/requests.reducers';

export default function Profile(props: { refund: any; readOnly?: boolean }) {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	interface extraPay extends Pay {}
	const pay: extraPay = useAppSelector(getNewPay);
	const refund = props.refund;
	let { id } = useParams();
	const beneficiary = useAppSelector(getBeneficiaryById(String(refund[PayEnum.beneficiaryId])));
	const Countries = _.keyBy(useAppSelector(getCountries), 'id');
	const Currencies = _.keyBy(useAppSelector(getCurrencies), 'id');
	const prefixFields = 'page.refund_profile.fields.';
	const prefixSection = 'page.refund_profile.';
	const prefixPaymentSection = 'page.refund_profile.payment.';
	const prefixPaymentFields = 'page.refund_profile.payment.fields.';

	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const purposeCodes = useAppSelector((state) => state.global.paymentPurpose);
	const language = localStorage.getItem('language');
	function formatValue(value?: any, key?: string) {
		let currency = Currencies[beneficiary?.currencyId || ''] || {};

		switch (key) {
			case PayEnum.amount:
				return refund?.currency?.symbol + getFormattedNumber(value);
			case PayEnum.fee:
				return `${currency?.symbol}${
					_.get(refund, 'fee.amount')
						? getFormattedNumber(_.get(refund, 'fee.amount'))
						: getFormattedNumber(value)
				}`;
			case PayEnum.iban:
				return beneficiary?.iban;
			case PayEnum.paymentDate:
			case PayEnum.createdAt:
			case PayEnum.dueDate:
			case PayEnum.settlementDate:
			case PayEnum.updatedAt:
				return value !== null && isValid(new Date(value)) ? format(new Date(value), timing.DATE) : value;
			case 'countryId':
			case 'bankAccountCountryId':
				return _.get(Countries[value || ''], 'name') || '';
			case 'currencyId':
				return _.get(Currencies[value || ''], 'name') || '';
			case PayEnum.paymentCategory:
				// For refunds, format the category using the helper if available
				const categoryOption = _.find(beneficiary?.paymentCategories as PaymentCategoryOption[], {
					id: String(value),
				});
				if (categoryOption) {
					return formatPaymentCategory(categoryOption, String(value), t);
				}
				return value;
			case PayEnum.status:
				return t('page.activity.table.status.' + _.snakeCase(value || '') + '.text');
			default:
				return value;
		}
	}
	return (
		<FormsStack>
			<InfoCard>
				<Stack direction={'row'} justifyContent={'space-between'} position={'relative'}>
					<FormHeader1 title={t(prefixSection + 'card_title.text')} />
					<Stack gap={1} position={'absolute'} right={0}>
						{!_.isNil(_.get(refund, 'file')) && _.has(refund, 'companyId') && (
							<FormButton autoheight="true" autowidth="true">
								<Button
									variant="outlined"
									onClick={() => {
										dispatch(
											getFile(
												_.get(refund, ['file', 'key']) || '',
												_.toNumber(_.get(refund, 'companyId'))
											)
										);
									}}
								>
									{`${t(prefixFields + 'invoice_file.text')}${
										!_.isNil(_.get(refund, 'fileSecond')) ? '  1' : ''
									}`}
								</Button>
							</FormButton>
						)}
					</Stack>
				</Stack>
				<Stack spacing={2}>
					<Paragraph3
						color={theme.palette.root.basicDeepBlue}
						title={t(prefixFields + _.snakeCase(PayEnum.createdAt) + '.text')}
						text={formatValue(_.get(refund, PayEnum.createdAt), PayEnum.createdAt)}
					/>
					{refund[PayEnum.settlementDate] && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixFields + _.snakeCase(PayEnum.settlementDate) + '.text')}
							text={formatValue(_.get(refund, PayEnum.settlementDate), PayEnum.settlementDate)}
						/>
					)}
					<Paragraph3
						color={theme.palette.root.basicDeepBlue}
						title={t(prefixFields + _.snakeCase(PayEnum.status) + '.text')}
						text={formatValue(_.get(refund, PayEnum.status), PayEnum.status)}
					/>
					<Paragraph3
						color={theme.palette.root.basicDeepBlue}
						title={t(prefixFields + PayEnum.amount + '.text')}
						text={formatValue(refund[PayEnum.amount], PayEnum.amount)}
					/>
					<Paragraph3
						color={theme.palette.root.basicDeepBlue}
						title={t(prefixFields + 'reason_title.text')}
						text={refund['note'] ?? t(prefixSection + 'reasons.' + refund['reason'] + '.text')}
					/>
					{refund['reference'] && (
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixFields + 'reference.text')}
							text={refund['reference']}
						/>
					)}
				</Stack>
			</InfoCard>
			{refund.payment && (
				<InfoCard>
					<Stack direction={'row'} justifyContent={'space-between'} position={'relative'}>
						<FormHeader1 title={t(prefixPaymentSection + 'card_title.text')} />
					</Stack>
					<Stack spacing={2}>
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixPaymentFields + _.snakeCase(PayEnum.createdAt) + '.text')}
							text={formatValue(_.get(refund, 'payment.' + PayEnum.createdAt), PayEnum.createdAt)}
						/>
						{refund[PayEnum.settlementDate] && (
							<Paragraph3
								color={theme.palette.root.basicDeepBlue}
								title={t(prefixPaymentFields + _.snakeCase(PayEnum.settlementDate) + '.text')}
								text={formatValue(
									_.get(refund, 'payment.' + PayEnum.settlementDate),
									PayEnum.settlementDate
								)}
							/>
						)}
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixPaymentFields + _.snakeCase(PayEnum.updatedAt) + '.text')}
							text={formatValue(_.get(refund, 'payment.' + PayEnum.updatedAt), PayEnum.updatedAt)}
						/>
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixPaymentFields + _.snakeCase(PayEnum.status) + '.text')}
							text={formatValue(_.get(refund, 'payment.' + PayEnum.status), PayEnum.status)}
						/>
						<Paragraph3
							color={theme.palette.root.basicDeepBlue}
							title={t(prefixPaymentFields + PayEnum.amount + '.text')}
							text={formatValue(_.get(refund, 'payment.' + PayEnum.amount), PayEnum.amount)}
						/>
						{_.get(refund, 'payment.reference') && (
							<Paragraph3
								color={theme.palette.root.basicDeepBlue}
								title={t(prefixPaymentFields + 'reference.text')}
								text={_.get(refund, 'payment.reference')}
							/>
						)}
					</Stack>
				</InfoCard>
			)}
		</FormsStack>
	);
}
